/**
 * Tiny DOM helpers for the loader (no framework, no innerHTML: sites with
 * Trusted Types enforced would reject string-to-HTML sinks).
 */

const SVG_NS = "http://www.w3.org/2000/svg";

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: Array<Node | string> = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value);
  for (const child of children) node.append(child);
  return node;
}

/** A 20x20 stroke icon from path data strings. */
export function icon(paths: string[], size = 18): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  for (const d of paths) {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    svg.append(path);
  }
  return svg;
}

/** Message-square-with-pen glyph used by the launcher. */
export const FEEDBACK_ICON = [
  "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  "M8 10h8",
  "M8 13h5",
];

/**
 * Apply styles that host-page CSS cannot override (inline + !important),
 * through setProperty so nothing is parsed from a style attribute string.
 */
export function lockStyles(node: HTMLElement, styles: Record<string, string>): void {
  for (const [prop, value] of Object.entries(styles)) node.style.setProperty(prop, value, "important");
}

/**
 * Attach a stylesheet to a shadow root. adoptedStyleSheets is not governed by
 * the host page's CSP; older browsers fall back to a nonce'd <style>.
 */
export function adoptStyles(root: ShadowRoot, css: string, nonce: string | undefined): void {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    return;
  } catch {
    /* fall through */
  }
  const style = document.createElement("style");
  if (nonce) style.nonce = nonce;
  style.textContent = css;
  root.append(style);
}
